/**
 * SRS Memory Dashboard
 *
 * Visualizes the decay algorithm, shows per-word SRS stats,
 * and lets users tune their personal decay rate.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { reviewApi, languagesApi } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Brain,
  TrendingDown,
  BarChart3,
  Settings2,
  Save,
  RotateCcw,
  Info,
} from 'lucide-react';

const STATUS_COLORS = {
  new: '#EF4444',
  learning: '#F97316',
  familiar: '#A855F7',
  known: '#8B5CF6',
  mastered: '#3B82F6',
};

function DecayCurveCanvas({ decayRate, stabilityMult, width = 600, height = 280 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    // Clear
    ctx.clearRect(0, 0, width, height);

    const pad = { top: 24, right: 20, bottom: 40, left: 50 };
    const plotW = width - pad.left - pad.right;
    const plotH = height - pad.top - pad.bottom;

    // Grid
    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
      const y = pad.top + (plotH / 4) * i;
      ctx.beginPath();
      ctx.moveTo(pad.left, y);
      ctx.lineTo(pad.left + plotW, y);
      ctx.stroke();
    }

    // Y axis labels
    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.font = '11px monospace';
    ctx.textAlign = 'right';
    for (let i = 0; i <= 4; i++) {
      const val = 1.0 - i * 0.25;
      const y = pad.top + (plotH / 4) * i;
      ctx.fillText(val.toFixed(2), pad.left - 8, y + 4);
    }

    // X axis labels (hours → days)
    ctx.textAlign = 'center';
    const maxHours = 720; // 30 days
    const dayMarks = [0, 1, 3, 7, 14, 30];
    for (const d of dayMarks) {
      const x = pad.left + (d * 24 / maxHours) * plotW;
      ctx.fillText(`${d}d`, x, height - 8);
    }

    // X axis title
    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.fillText('Days since last review', pad.left + plotW / 2, height - 0);

    // Draw curves for different stability values
    const stabilities = [
      { val: 1.0, color: '#EF4444', label: 'Low stability (1.0)' },
      { val: 3.0, color: '#F97316', label: 'Medium stability (3.0)' },
      { val: 6.0, color: '#22C55E', label: 'High stability (6.0)' },
      { val: 10.0, color: '#3B82F6', label: 'Max stability (10.0)' },
    ];

    const decay = (strength, stability, hours) => {
      const effStab = Math.max(0.5, Math.min(stability * stabilityMult, 10.0));
      const effDecay = decayRate;
      return strength * Math.exp(-effDecay * hours / effStab);
    };

    // Draw each curve
    for (const curve of stabilities) {
      ctx.beginPath();
      ctx.strokeStyle = curve.color;
      ctx.lineWidth = 2;

      for (let px = 0; px <= plotW; px++) {
        const hours = (px / plotW) * maxHours;
        const str = decay(1.0, curve.val, hours);
        const x = pad.left + px;
        const y = pad.top + (1.0 - str) * plotH;
        if (px === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }

    // Threshold lines
    const thresholds = [
      { val: 0.9, label: 'Mastered', color: '#3B82F6' },
      { val: 0.7, label: 'Known', color: '#8B5CF6' },
      { val: 0.4, label: 'Familiar', color: '#A855F7' },
      { val: 0.1, label: 'Learning', color: '#F97316' },
    ];
    for (const th of thresholds) {
      const y = pad.top + (1.0 - th.val) * plotH;
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = th.color + '40';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(pad.left, y);
      ctx.lineTo(pad.left + plotW, y);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = th.color + '80';
      ctx.font = '9px monospace';
      ctx.textAlign = 'left';
      ctx.fillText(th.label, pad.left + plotW + 2, y + 3);
    }

    // Legend
    ctx.font = '10px monospace';
    const legendX = pad.left + 8;
    let legendY = pad.top + 12;
    for (const curve of stabilities) {
      ctx.fillStyle = curve.color;
      ctx.fillRect(legendX, legendY - 6, 12, 3);
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      ctx.textAlign = 'left';
      ctx.fillText(curve.label, legendX + 16, legendY);
      legendY += 14;
    }
  }, [decayRate, stabilityMult, width, height]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width, height }}
      className="rounded-lg"
      data-testid="decay-curve-canvas"
    />
  );
}

function DistributionBar({ distribution }) {
  const total = Object.values(distribution).reduce((a, b) => a + b, 0);
  if (total === 0) return <div className="h-6 bg-muted rounded-full" />;

  const segments = [
    { key: 'mastered', color: STATUS_COLORS.mastered },
    { key: 'known', color: STATUS_COLORS.known },
    { key: 'familiar', color: STATUS_COLORS.familiar },
    { key: 'learning', color: STATUS_COLORS.learning },
    { key: 'new', color: STATUS_COLORS.new },
  ];

  return (
    <div className="space-y-2">
      <div className="flex h-6 rounded-full overflow-hidden" data-testid="strength-distribution-bar">
        {segments.map(({ key, color }) => {
          const pct = ((distribution[key] || 0) / total) * 100;
          if (pct === 0) return null;
          return (
            <div
              key={key}
              style={{ width: `${pct}%`, backgroundColor: color }}
              className="transition-all duration-500"
              title={`${key}: ${distribution[key]} (${pct.toFixed(1)}%)`}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-3 text-xs">
        {segments.map(({ key, color }) => (
          <span key={key} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
            <span className="text-muted-foreground capitalize">{key}</span>
            <span className="font-mono">{distribution[key] || 0}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function MemoryDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [languages, setLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState('');
  const [decayRate, setDecayRate] = useState(0.001);
  const [stabilityMult, setStabilityMult] = useState(1.0);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedLang ? { language_id: selectedLang } : {};
      const [statsRes, settingsRes] = await Promise.all([
        reviewApi.getSrsStats(params),
        reviewApi.getSrsSettings(),
      ]);
      setStats(statsRes.data);
      setDecayRate(settingsRes.data.personal_decay_rate);
      setStabilityMult(settingsRes.data.stability_multiplier);
      setDirty(false);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load SRS stats');
    } finally {
      setLoading(false);
    }
  }, [selectedLang]);

  useEffect(() => {
    languagesApi.getUserLanguages().then(res => {
      const raw = Array.isArray(res.data) ? res.data : (res.data.languages || []);
      setLanguages(raw.map(e => ({
        language_id: e.language_id || e.language?.language_id,
        name: e.language?.name || e.name,
        code: e.language?.code || e.code,
      })));
    }).catch(() => {});
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await reviewApi.updateSrsSettings({
        personal_decay_rate: decayRate,
        stability_multiplier: stabilityMult,
      });
      toast.success('SRS settings saved');
      setDirty(false);
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setDecayRate(0.001);
    setStabilityMult(1.0);
    setDirty(true);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-72" />
      </div>
    );
  }

  const accuracy = stats?.accuracy || 0;
  const accuracyColor = accuracy >= 0.85 ? 'text-green-400' : accuracy >= 0.7 ? 'text-yellow-400' : 'text-red-400';

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="memory-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Brain className="h-6 w-6 text-purple-400" />
          <h1 className="text-2xl font-bold">Memory & SRS</h1>
        </div>
        <Select
          value={selectedLang || 'all'}
          onValueChange={(v) => setSelectedLang(v === 'all' ? '' : v)}
        >
          <SelectTrigger className="w-48" data-testid="memory-lang-filter">
            <SelectValue placeholder="All languages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All languages</SelectItem>
            {languages.map(l => (
              <SelectItem key={l.language_id} value={l.code || l.language_id}>
                {l.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Key Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold font-mono">{stats?.total_words || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Total Words</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <p className={`text-3xl font-bold font-mono ${accuracyColor}`}>
              {(accuracy * 100).toFixed(1)}%
            </p>
            <p className="text-xs text-muted-foreground mt-1">Review Accuracy</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold font-mono text-purple-400">
              {(stats?.avg_strength || 0).toFixed(3)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Avg Strength</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold font-mono text-blue-400">
              {(stats?.avg_stability || 0).toFixed(1)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Avg Stability</p>
          </CardContent>
        </Card>
      </div>

      {/* Strength Distribution */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Strength Distribution
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DistributionBar distribution={stats?.distribution || {}} />
          <div className="flex justify-between mt-3 text-xs text-muted-foreground">
            <span>{stats?.total_reviews || 0} total reviews</span>
            <span>{stats?.total_correct || 0} correct / {stats?.total_failed || 0} failed</span>
          </div>
        </CardContent>
      </Card>

      {/* Forgetting Curve */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingDown className="h-4 w-4" />
            Forgetting Curve
            <span className="text-xs font-normal text-muted-foreground ml-2">
              How strength decays over time at different stability levels
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center overflow-x-auto">
            <DecayCurveCanvas
              decayRate={decayRate}
              stabilityMult={stabilityMult}
              width={Math.min(640, typeof window !== 'undefined' ? window.innerWidth - 80 : 600)}
              height={280}
            />
          </div>
          <div className="mt-3 p-3 rounded-lg bg-muted/30 text-xs text-muted-foreground flex items-start gap-2">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              <strong>Formula:</strong> strength × e<sup>(-decay_rate × hours / stability)</sup>.
              Higher stability = slower forgetting. Mastered words decay 90% slower, known words 50% slower.
              Adjust the controls below to see how the curve changes.
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Tuning Controls */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Settings2 className="h-4 w-4" />
            Decay Tuning
            <span className="text-xs font-normal text-muted-foreground ml-2">
              Adjust how fast you forget
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Decay Rate */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">Decay Rate</label>
              <span className="text-xs font-mono text-muted-foreground">{decayRate.toFixed(4)}</span>
            </div>
            <input
              type="range"
              min="0.0001"
              max="0.01"
              step="0.0001"
              value={decayRate}
              onChange={(e) => { setDecayRate(parseFloat(e.target.value)); setDirty(true); }}
              className="w-full accent-purple-500"
              data-testid="decay-rate-slider"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>Slow forgetting</span>
              <span>Fast forgetting</span>
            </div>
          </div>

          {/* Stability Multiplier */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">Stability Multiplier</label>
              <span className="text-xs font-mono text-muted-foreground">{stabilityMult.toFixed(2)}x</span>
            </div>
            <input
              type="range"
              min="0.25"
              max="4.0"
              step="0.05"
              value={stabilityMult}
              onChange={(e) => { setStabilityMult(parseFloat(e.target.value)); setDirty(true); }}
              className="w-full accent-blue-500"
              data-testid="stability-mult-slider"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>Weaker memory</span>
              <span>Stronger memory</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <Button
              onClick={handleSave}
              disabled={!dirty || saving}
              className="gap-2"
              data-testid="save-srs-settings-btn"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
            <Button
              variant="outline"
              onClick={handleReset}
              className="gap-2"
              data-testid="reset-srs-settings-btn"
            >
              <RotateCcw className="h-4 w-4" />
              Reset to Default
            </Button>
            {dirty && (
              <span className="text-xs text-amber-400 animate-pulse">Unsaved changes</span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
